<script lang="ts">
	/**
	 * Light / dark toggle, in the desktop utility bar and the mobile menu.
	 *
	 * Three states, not two: `system` follows the OS and is the default, and an
	 * explicit `light` or `dark` overrides it. A two-state toggle silently
	 * overrides the OS the first time it is touched and can never hand control
	 * back.
	 *
	 * The `dark` class on <html> is what `:root.dark` in `app.css` keys off, and
	 * it is applied by the inline pre-paint script in `app.html`, not here — the
	 * site is prerendered, so a component running after hydration would paint the
	 * light theme first and then flip it. This handles changes only. `theme` is
	 * the single key both places read and write; keep them in sync.
	 *
	 * State is seeded at component init rather than in `onMount` so the very
	 * first client render already shows the right icon. Seeding in `onMount`
	 * instead left the monitor glyph on screen for a frame after the page had
	 * already painted dark.
	 */
	import { onMount } from 'svelte';
	import { browser } from '$app/environment';
	import { m } from '$lib/paraglide/messages';

	type Theme = 'light' | 'dark' | 'system';

	const prefersDark = () =>
		typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

	/**
	 * Only `light` and `dark` are ever stored; `system` is the absence of the
	 * key, which is what lets it keep following the OS. Anything else is treated
	 * as absent rather than trusted — it is user-writable storage.
	 */
	function stored(): Theme {
		if (!browser) return 'system';
		try {
			const value = localStorage.getItem('theme');
			if (value === 'light' || value === 'dark') return value;
		} catch {
			/* storage unavailable — fall back to following the OS */
		}
		return 'system';
	}

	/** Safe on the server: `prefersDark` is false there, so this yields `light`. */
	const resolve = (value: Theme): 'light' | 'dark' =>
		value === 'system' ? (prefersDark() ? 'dark' : 'light') : value;

	const initial = stored();

	let theme = $state<Theme>(initial);
	let resolved = $state<'light' | 'dark'>(resolve(initial));

	function apply(next: Theme) {
		theme = next;
		resolved = resolve(next);

		document.documentElement.classList.toggle('dark', resolved === 'dark');

		try {
			if (next === 'system') localStorage.removeItem('theme');
			else localStorage.setItem('theme', next);
		} catch {
			/* storage unavailable — the choice just will not persist */
		}
	}

	onMount(() => {
		// Keep following the OS while the visitor has not chosen explicitly.
		const media = window.matchMedia('(prefers-color-scheme: dark)');
		const onChange = () => {
			if (theme === 'system') apply('system');
		};
		media.addEventListener('change', onChange);
		return () => media.removeEventListener('change', onChange);
	});

	/** Cycles light → dark → system, so the OS default stays reachable. */
	function next() {
		apply(theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light');
	}

	const label = $derived(
		theme === 'system' ? m.theme_system() : theme === 'dark' ? m.theme_dark() : m.theme_light()
	);
</script>

<!--
	Styled to match `LanguageSwitcher`'s trigger — the same bordered, shadowed
	control at the same height — because the two sit side by side and are the
	same kind of thing: a preference that persists past this page.
-->
<button
	type="button"
	onclick={next}
	title={label}
	aria-label={label}
	class="inline-flex cursor-pointer items-center rounded-md border border-subtle bg-surface p-2 text-body shadow-sm transition hover:bg-surface-muted"
>
	{#if theme === 'system'}
		<!-- Monitor: following the OS -->
		<svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
			<path
				stroke-linecap="round"
				stroke-linejoin="round"
				stroke-width="1.5"
				d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25A2.25 2.25 0 0 1 5.25 3h13.5A2.25 2.25 0 0 1 21 5.25Z"
			/>
		</svg>
	{:else if resolved === 'dark'}
		<!-- Moon -->
		<svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
			<path
				stroke-linecap="round"
				stroke-linejoin="round"
				stroke-width="1.5"
				d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"
			/>
		</svg>
	{:else}
		<!-- Sun -->
		<svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
			<path
				stroke-linecap="round"
				stroke-linejoin="round"
				stroke-width="1.5"
				d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
			/>
		</svg>
	{/if}
</button>
